import { parseLocalDateInput } from "./dateUtils";

const VALID_HOUR_PATTERN = /^\d{2}:\d{2}$/;

export const WEEKDAY_HOURS = Object.freeze([
  "08:20",
  "09:10",
  "10:00",
  "10:50",
  "11:40",
  "12:30",
  "13:20",
  "14:10",
  "15:00",
  "15:50",
  "16:40",
  "17:30",
  "18:20",
  "19:10",
  "20:00",
]);

export const SATURDAY_HOURS = Object.freeze([
  "07:00",
  "07:50",
  "08:40",
  "09:30",
  "10:20",
  "11:10",
  "12:00",
  "12:50",
  "13:40",
  "14:30",
  "15:20",
  "16:10",
  "17:00",
  "17:50",
  "18:40",
]);

export const SERVICE_SLOT_LENGTH = Object.freeze({
  Cabelo: 1,
  Barba: 1,
  "Cabelo e Barba": 2,
  "Prótese + Corte": 3,
  "Manutenção da Prótese": 2,
});

const normalizeSlotPart = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\//g, "-")
    .replace(/\s+/g, "-");
};

export const normalizeAppointmentHours = (hour) => {
  const hours = Array.isArray(hour) ? hour : [hour];

  return hours
    .filter((value) => typeof value === "string")
    .map((value) => value.trim())
    .filter((value) => VALID_HOUR_PATTERN.test(value));
};

export const hasValidAppointmentHours = (hour) => {
  const hours = Array.isArray(hour) ? hour : [hour];

  return (
    hours.length > 0 &&
    hours.every((value) => (
      typeof value === "string" &&
      VALID_HOUR_PATTERN.test(value.trim())
    ))
  );
};

export const getServiceSlotLength = (service) => {
  return SERVICE_SLOT_LENGTH[service] || 1;
};

export const getAppointmentHoursForService = ({
  startHour,
  service,
  availableHours,
}) => {
  const normalizedStartHour = normalizeAppointmentHours(startHour)[0];
  const normalizedAvailableHours = normalizeAppointmentHours(availableHours);
  const slotLength = getServiceSlotLength(service);
  const startIndex = normalizedAvailableHours.indexOf(normalizedStartHour);

  if (startIndex === -1) {
    return [];
  }

  const selectedHours = normalizedAvailableHours.slice(startIndex, startIndex + slotLength);

  if (selectedHours.length !== slotLength) {
    return [];
  }

  return selectedHours;
};

export const getOccupiedAppointmentHours = (appointments = [], extraOccupiedHours = []) => {
  const occupiedHours = new Set([
    ...normalizeAppointmentHours(extraOccupiedHours),
    ...appointments.flatMap((appointment) => normalizeAppointmentHours(appointment?.hour)),
  ]);

  return Array.from(occupiedHours);
};

export const isServiceStartAvailable = ({
  startHour,
  service,
  availableHours,
  occupiedHours = [],
}) => {
  const selectedHours = getAppointmentHoursForService({
    startHour,
    service,
    availableHours,
  });
  const occupiedSet = new Set(normalizeAppointmentHours(occupiedHours));

  return (
    selectedHours.length === getServiceSlotLength(service) &&
    selectedHours.every((hour) => !occupiedSet.has(hour))
  );
};

export const getAvailableStartHours = ({
  service,
  availableHours,
  occupiedHours = [],
}) => {
  if (!service) {
    return [];
  }

  return normalizeAppointmentHours(availableHours).filter((startHour) => (
    isServiceStartAvailable({
      startHour,
      service,
      availableHours,
      occupiedHours,
    })
  ));
};

export const getTerminalBlockedHours = (availableHours = []) => {
  const normalizedAvailableHours = normalizeAppointmentHours(availableHours);

  if (normalizedAvailableHours.length === 0) {
    return [];
  }

  return [normalizedAvailableHours[normalizedAvailableHours.length - 1]];
};

export const getBusinessHoursForDate = (date) => {
  if (!date) {
    return [];
  }

  const selectedDate = parseLocalDateInput(date);

  if (!selectedDate) {
    return [];
  }

  return selectedDate.getDay() === 6 ? [...SATURDAY_HOURS] : [...WEEKDAY_HOURS];
};

export const buildAppointmentSlotId = ({ date, professional, hour }) => {
  return [
    normalizeSlotPart(date),
    normalizeSlotPart(professional),
    normalizeSlotPart(hour),
  ].join("_");
};

export const buildAppointmentSlots = (appointment) => {
  const hours = normalizeAppointmentHours(appointment.hour);

  return hours.map((hour) => ({
    id: buildAppointmentSlotId({
      date: appointment.date,
      professional: appointment.professional,
      hour,
    }),
    date: appointment.date,
    professional: appointment.professional,
    hour,
  }));
};

export const buildDayOffId = ({ date, professional }) => {
  return [
    normalizeSlotPart(date),
    normalizeSlotPart(professional),
  ].join("_");
};

export const buildDayOffSlots = (dayOff) => {
  return getBusinessHoursForDate(dayOff.date).map((hour) => ({
    id: buildAppointmentSlotId({
      date: dayOff.date,
      professional: dayOff.professional,
      hour,
    }),
    date: dayOff.date,
    professional: dayOff.professional,
    hour,
  }));
};
